import {
  type AirlineDetail,
  AirlineDetailSchema,
  type AirlineListQuery,
  type AirlineListResponse,
  AirlineListResponseSchema,
  type CountryOption,
  CountryOptionListSchema,
  type CreateAirlineRequest,
  type UpdateAirlineRequest,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Airline master API. Every response is validated with the shared Zod schema
 * so a server change can't sneak past the UI types.
 */

export const listAirlines = async (query: AirlineListQuery): Promise<AirlineListResponse> => {
  const { data } = await apiClient.get<unknown>('/airlines', { params: query });
  return AirlineListResponseSchema.parse(data);
};

export const getAirline = async (id: string): Promise<AirlineDetail> => {
  const { data } = await apiClient.get<unknown>(`/airlines/${id}`);
  return AirlineDetailSchema.parse(data);
};

export const createAirline = async (payload: CreateAirlineRequest): Promise<AirlineDetail> => {
  const { data } = await apiClient.post<unknown>('/airlines', payload);
  return AirlineDetailSchema.parse(data);
};

export const updateAirline = async (
  id: string,
  payload: UpdateAirlineRequest,
): Promise<AirlineDetail> => {
  const { data } = await apiClient.patch<unknown>(`/airlines/${id}`, payload);
  return AirlineDetailSchema.parse(data);
};

export const deleteAirline = async (id: string): Promise<void> => {
  await apiClient.delete(`/airlines/${id}`);
};

export const bulkDeleteAirlines = async (
  ids: string[],
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/airlines/bulk-delete', { ids });
  return data;
};

export const bulkSetAirlinesStatus = async (
  ids: string[],
  isActive: boolean,
): Promise<{ updated: number; failed: { id: string; error: string }[] }> => {
  const { data } = await apiClient.post<{
    updated: number;
    failed: { id: string; error: string }[];
  }>('/airlines/bulk-set-status', { ids, isActive });
  return data;
};

export const listCountryOptions = async (): Promise<CountryOption[]> => {
  const { data } = await apiClient.get<unknown>('/countries/options');
  return CountryOptionListSchema.parse(data);
};
